import { useEffect, useState } from "react";
import { Gift, Sparkles } from "lucide-react";
import { useCart } from "../lib/cart";
import api from "../lib/api";

/**
 * Dynamic bundle banner — shown above gallery views (Search, Event, Photographer profile).
 * Behavior:
 *  - If cart is empty: show plain pitch ("5 foton 269 kr · 10 foton 449 kr")
 *  - If cart has items but no bundle unlocked: show "Lägg X foton till spara Y%"
 *  - If cart has a bundle unlocked: show "Du sparar Y% med X-pack"
 */
export default function BundleBanner({ photographerId = null, unitPrice = null, className = "" }) {
  const { items } = useCart();
  const [bundles, setBundles] = useState([]);
  const [defaultPrice, setDefaultPrice] = useState(69);

  useEffect(() => {
    api.get("/bundles").then((r) => {
      setBundles(r.data.bundles || []);
      if (r.data.default_price_sek) setDefaultPrice(r.data.default_price_sek);
    }).catch(() => {});
  }, []);

  if (!bundles.length) return null;

  // Filter cart for this photographer if specified, else all
  const relevantItems = photographerId
    ? items.filter((i) => i.photographer_id === photographerId)
    : items;

  // Group by photographer to find best bundle situation
  const byPhotog = {};
  for (const it of relevantItems) {
    byPhotog[it.photographer_id] = byPhotog[it.photographer_id] || [];
    byPhotog[it.photographer_id].push(it);
  }

  // Pick the photographer with most items (most likely to unlock)
  let best = null;
  for (const [pid, list] of Object.entries(byPhotog)) {
    if (!best || list.length > best.list.length) {
      best = { pid, list };
    }
  }

  // No items → static pitch
  if (!best || best.list.length === 0) {
    const pitches = bundles.map((b) => {
      const saving = Math.round((b.discount_pct || 0) * 100);
      return `${b.min_qty} foton -${saving}%`;
    });
    return (
      <div
        className={`bg-brand/10 border-l-4 border-brand p-3 flex items-center gap-3 ${className}`}
        data-testid="bundle-banner-static"
      >
        <Gift className="w-5 h-5 text-brand flex-shrink-0" />
        <div className="flex-1 text-sm text-white/85">
          <span className="font-bold text-brand uppercase tracking-wider text-xs">Paketpris</span>
          <span className="mx-2 text-white/40">·</span>
          {pitches.join(" · ")}
        </div>
      </div>
    );
  }

  // We have items from a photographer — check unit price uniformity & match against bundles
  const samePrice = new Set(best.list.map((i) => i.price_sek)).size === 1;
  const unit = best.list[0].price_sek;
  const qty = best.list.length;

  if (!samePrice) return null;

  // Find any currently-unlocked bundle
  let unlocked = null;
  for (const b of bundles) {
    if (qty >= b.min_qty && qty <= b.max_qty) {
      const regularTotal = qty * unit;
      const bundleTotal = regularTotal * (1 - (b.discount_pct || 0));
      if (bundleTotal < regularTotal) {
        const saving = Math.round((b.discount_pct || 0) * 100);
        unlocked = { bundle: b, saving, regularTotal, bundleTotal };
        break;
      }
    }
  }

  if (unlocked) {
    return (
      <div
        className={`bg-green-700/15 border-l-4 border-green-500 p-3 flex items-center gap-3 ${className}`}
        data-testid="bundle-banner-unlocked"
      >
        <Sparkles className="w-5 h-5 text-green-400 flex-shrink-0" />
        <div className="flex-1 text-sm text-white/90">
          <span className="font-bold text-green-400 uppercase tracking-wider text-xs">
            {unlocked.bundle.name} aktivt
          </span>
          <span className="mx-2 text-white/40">·</span>
          Du sparar <strong className="text-green-400">{unlocked.saving}%</strong> ({Math.round(unlocked.regularTotal - unlocked.bundleTotal)} kr) just nu.
        </div>
      </div>
    );
  }

  // Suggest next bundle if close to unlocking
  let suggestion = null;
  for (const b of bundles) {
    if (qty < b.min_qty) {
      const missing = b.min_qty - qty;
      const saving = Math.round((b.discount_pct || 0) * 100);
      const samplePrice = Math.round(b.min_qty * unit * (1 - (b.discount_pct || 0)));
      if (!suggestion || missing < suggestion.missing) {
        suggestion = { bundle: b, missing, saving, samplePrice };
      }
    }
  }

  if (suggestion) {
    return (
      <div
        className={`bg-brand/15 border-l-4 border-brand p-3 flex items-center gap-3 ${className}`}
        data-testid="bundle-banner-suggestion"
      >
        <Gift className="w-5 h-5 text-brand flex-shrink-0" />
        <div className="flex-1 text-sm text-white/90">
          <span className="font-bold text-brand uppercase tracking-wider text-xs">Tips</span>
          <span className="mx-2 text-white/40">·</span>
          Lägg <strong className="text-brand">{suggestion.missing}</strong> {suggestion.missing === 1 ? "foto" : "foton"} till för <strong className="text-brand">{suggestion.bundle.name}</strong> <span className="text-white/60">(spara {suggestion.saving}%)</span>
        </div>
      </div>
    );
  }

  return null;
}
